import Trix from 'trix';
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { renderToString } from 'react-dom/server';
import './Wysiwyg.scss';

const useUnique = (n = 1000) => {
  const randomRef = React.useRef(null);

  if (!randomRef.current) {
    randomRef.current = Math.floor(Math.random() * n);
  }

  return randomRef.current;
};

const Wysiwyg = props => {
  const uniqueId = `trix-${useUnique()}`;

  const [editMode, setEditMode] = React.useState(() => {
    // If all the HTML is supported by Trix, load up the Trix editor. Otherwise,
    // default to plain textarea (raw)
    const sanitizedHTML = Trix.HTMLSanitizer.sanitize(props.value).getHTML();
    return sanitizedHTML === props.value ? 'wysiwyg' : 'raw';
  });

  const wysiwygEditor = useRef(null);

  function handleFieldChange(event) {
    props.onChange(event);
  }

  function handleToggleFontSize() {
    const fontOptions = document.getElementById('trix-font-options');
    fontOptions.style.display = fontOptions.style.display === 'none' ? 'block' : 'none';
  }

  function handleTrixInit(event) {
    Trix.config.blockAttributes.heading2 = {
      tagName: 'h2',
      parser: function(element) {
        return element.style.tagName === 'h2';
      },
      inheritable: true,
      terminal: true
    };

    Trix.config.blockAttributes.heading3 = {
      tagName: 'h3',
      parser: function(element) {
        return element.style.tagName === 'h3';
      },
      inheritable: true,
      terminal: true
    };

    Trix.config.blockAttributes.heading4 = {
      tagName: 'h4',
      parser: function(element) {
        return element.style.tagName === 'h4';
      },
      inheritable: true,
      terminal: true
    };

    const fontSizes = renderToString(
      <div
        id="trix-font-size"
        className="trix-button trix-button--icon trix-dropdown"
      >
        aA

        <ul
          id="trix-font-options"
          style={{display: 'none'}}
          className="trix-dropdown-options"
        >
          <li
            data-trix-attribute="heading1"
          >
            Title
          </li>
          <li
            data-trix-attribute="heading2"
          >
            Subtitle
          </li>
          <li
            data-trix-attribute="heading3"
          >
            Heading 1
          </li>
          <li
            data-trix-attribute="heading4"
          >
            Heading 2
          </li>
        </ul>
      </div>
    );

    event.target.toolbarElement
      .querySelector('.trix-button-group--block-tools')
      .insertAdjacentHTML('afterBegin', fontSizes);

    event.target.editor.loadHTML(props.value);

    const fontSizeToggle = document.getElementById('trix-font-size');
    fontSizeToggle.addEventListener('click', handleToggleFontSize);
  }

  React.useEffect(() => {
    wysiwygEditor.current.addEventListener('trix-initialize', handleTrixInit);
    wysiwygEditor.current.addEventListener('trix-change', handleFieldChange);

    return function cleanup() {
      const fontSizeToggle = document.getElementById('trix-font-size');

      if (fontSizeToggle) {
        fontSizeToggle.removeEventListener('click', handleToggleFontSize);
      }

      if (wysiwygEditor.current) {
        wysiwygEditor.current.removeEventListener(
          'trix-initialize',
          handleTrixInit
        );
        wysiwygEditor.current.removeEventListener(
          'trix-change',
          handleFieldChange
        );
      }
    };
  }, []);

  return (
    <div className="col-xs-12 mt2" ref={wysiwygEditor}>
      {editMode === 'wysiwyg' ? (
        <div>
          <input id={uniqueId} type="hidden" name="isiText" />
          <trix-editor
            name="isiText"
            placeholder={props.placeholder}
            input={uniqueId}
          />
        </div>
      ) : (
        <textarea
          placeholder={props.placeholder}
          onChange={handleFieldChange}
          name="isiText"
          value={props.value}
        />
      )}
      <div className="ml2 mt2 mb2">
        <label className="mr3 clickable">
          <input
            className="mr1"
            type="radio"
            checked={editMode === 'raw'}
            onChange={() => {
              setEditMode('raw');
            }}
          />
          HTML Text Editor
        </label>
        <label className="clickable">
          <input
            className="mr1"
            type="radio"
            checked={editMode === 'wysiwyg'}
            onChange={() => {
              setEditMode('wysiwyg');
            }}
          />
          Rich Text Editor
        </label>
      </div>
    </div>
  );
};

Wysiwyg.propTypes = {
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

export default Wysiwyg;
